import _template from 'lodash/template';
import { match } from 'ts-pattern';
import { TemplateExecutor } from 'lodash';
import { RECURRENCE_TYPE_LABEL_MAPPER } from 'modules/campaigns/editor/modals/ConfirmScheduleCampaignModal/ConfirmScheduleCampaignModal.constants';
import { EndRecurrenceType } from 'modules/campaigns/editor/types';
import { RecurrenceScheduleType, GraphQLCampaign } from 'modules/campaigns/lib/types';
import { getDayRecurrenceFrequency } from 'modules/campaignIndex/components/CampaignLaunchTableCell';
import {
  CAMPAIGN_DATE_FORMAT_WITH_TZ,
  CAMPAIGN_FULL_DATE_FORMAT,
  CAMPAIGN_FULL_DATE_FORMAT_WITH_TZ,
  parseISOZonedDate,
} from 'utils/date';
import momentTz, { Moment, MomentInput } from 'moment-timezone';
import { ExperimentVariant, ScheduleType } from 'modules/domains/campaign';
import { Nullish } from 'utils/tsHelpers';
import { getSendTimeExperimentVariants } from 'modules/campaigns/editor/utils';

export const END_SCHEDULE_RECURRENCE_TYPE_TEXT_MAPPER = {
  [EndRecurrenceType.Never]: 'does not end',
  [EndRecurrenceType.OnDate]: _template('<%= endDate %>'),
};

/**
 * Returns recurrence frequency text
 */
export const getScheduleRecurrenceFrequencyText = (
  recurrenceScheduleType: RecurrenceScheduleType,
  campaign: GraphQLCampaign,
) => {
  const textOrTemplate = RECURRENCE_TYPE_LABEL_MAPPER?.[recurrenceScheduleType];

  const onCustomRecurrenceScheduleType = () => {
    const recurrenceFrequency = getDayRecurrenceFrequency(
      campaign?.schedulerMsgParams as unknown as any,
    );

    return (textOrTemplate as TemplateExecutor)({
      recurrenceFrequency,
      pluralLetter: `${recurrenceFrequency === 1 ? '' : 's'}`,
    });
  };

  return match(recurrenceScheduleType)
    .with(RecurrenceScheduleType.Custom, onCustomRecurrenceScheduleType)
    .otherwise(() => textOrTemplate as string);
};

/**
 *
 */
export const getScheduleEndRecurrenceTypeText = (
  endRecurrenceType: EndRecurrenceType,
  campaign: GraphQLCampaign,
  timezone: string,
): string => {
  const textOrTemplate = END_SCHEDULE_RECURRENCE_TYPE_TEXT_MAPPER?.[endRecurrenceType];

  const onDate = () => {
    const date = parseISOZonedDate(campaign?.schedulerMsgParams?.endDate?.timestamp as string);

    /**
     * NOTE: the time is always displayed as eod (ex: 11:59 PM)
     * @see https://github.com/Iterable/iterable/blob/bdd3901c6e7d05375038be73e7345830ec20b67d/app/views/campaigns/reviewCampaign.scala.html#L297
     *  */
    const endDate = date?.tz(timezone).endOf('day').format(CAMPAIGN_DATE_FORMAT_WITH_TZ);

    return (textOrTemplate as TemplateExecutor)({
      endDate,
    });
  };

  return match(endRecurrenceType)
    .with(EndRecurrenceType.OnDate, onDate)
    .otherwise(() => textOrTemplate as string);
};

/**
 * Returns blast campaign launch time display text
 */
export const getLaunchText = (date: Moment, isRecipientTimeZone: boolean) => {
  return isRecipientTimeZone
    ? `${date.format(CAMPAIGN_FULL_DATE_FORMAT)} in the recipients' time zone`
    : `${date.format(CAMPAIGN_FULL_DATE_FORMAT_WITH_TZ)} (approximately ${date.fromNow()})`;
};

export const getNextSendDateText = (date: string, projectTimeZone: string) => {
  const nextRunDate = momentTz(date).tz(projectTimeZone);
  return `${nextRunDate.format(
    CAMPAIGN_FULL_DATE_FORMAT_WITH_TZ,
  )} (approximately ${nextRunDate.fromNow()})`;
};

export const getTimeZoneName = (tz: string) => momentTz()?.tz(tz)?.tz();
export const getZoneAbbr = (tz: string) => momentTz()?.tz(tz)?.zoneAbbr();

export const getIsRecipientTimeZoneScheduleType = (campaign: GraphQLCampaign) =>
  campaign?.schedulerMsgParams?.scheduleType === ScheduleType.RecipientTimeZone;

export const getEndedTimeDisplayText = (date: string, projectTimeZone: string) =>
  momentTz(date)?.tz(projectTimeZone)?.format(CAMPAIGN_FULL_DATE_FORMAT_WITH_TZ);

/**
 * Returns the experiment variant with the earliest send time
 */
export const getEarliestSendTimeExperimentVariant = (
  experiments: GraphQLCampaign['experiments'],
): Nullish<ExperimentVariant> => {
  const sendTimeExperimentVariants = getSendTimeExperimentVariants(experiments);
  const earliestSendTimeVariants = sendTimeExperimentVariants?.sort?.((variantA, variantB) => {
    const a = parseISOZonedDate(variantA.sendTime as string);
    const b = parseISOZonedDate(variantB.sendTime as string);
    return a.diff(b);
  });

  return earliestSendTimeVariants?.[0];
};

/**
 * Returns campaign's launch time or earliest send time experiment variant's display text
 */
export const getLaunchTimeDisplayText = (
  startAt: MomentInput,
  targetTimeZone: string,
  isRecipientTimeZone: boolean,
  experiments: GraphQLCampaign['experiments'],
) => {
  if (startAt || experiments) {
    const startDate = momentTz(startAt).tz(targetTimeZone);
    const campaignLaunchDate = getLaunchText(startDate, isRecipientTimeZone);

    const earliestSendTimeVariant = getEarliestSendTimeExperimentVariant(experiments);
    const experimentLaunchDate = earliestSendTimeVariant
      ? getLaunchText(parseISOZonedDate(earliestSendTimeVariant?.sendTime as string), false) // TODO: add support for experiments with recipient time zone
      : null;

    return experimentLaunchDate || campaignLaunchDate;
  }
};
